import { useLayoutEffect, useState } from 'react'

/**
 * The content-box width of an element, kept current as it resizes. Null until
 * the element attaches and is measured.
 *
 * Returns a callback ref for the same reason `useStickyColumnEdge` does: the
 * measured element can render conditionally, and a `useRef` is still null on
 * the effect pass after its first render.
 */
export function useContainerWidth<T extends HTMLElement>(): {
  ref: (node: T | null) => void
  width: number | null
} {
  const [node, setNode] = useState<T | null>(null)
  const [width, setWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (!node) return
    // Content box, not border box: the field grid's padding is not space a
    // field can take.
    const style = getComputedStyle(node)
    const padding = parseFloat(style.paddingLeft) + parseFloat(style.paddingRight)
    setWidth(node.clientWidth - padding)
    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) setWidth(entry.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [node])

  return { ref: setNode, width }
}
